import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ClientDetails = ({ client, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [newNote, setNewNote] = useState('');
  const [isEditingContact, setIsEditingContact] = useState(false);

  if (!client) {
    return (
      <div className="h-full flex items-center justify-center bg-card border-l border-border">
        <div className="text-center">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">
            Selecione um cliente para ver os detalhes
          </p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Visão Geral', icon: 'Eye' },
    { id: 'payments', label: 'Pagamentos', icon: 'CreditCard' },
    { id: 'notes', label: 'Notas', icon: 'FileText' },
    { id: 'documents', label: 'Documentos', icon: 'Paperclip' }
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(value);
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('pt-BR');
  };

  const handleAddNote = () => {
    if (newNote?.trim()) {
      console.log('Adding note:', newNote);
      setNewNote('');
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="bg-muted/50 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-foreground mb-4">Informações de Contato</h3>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Icon name="Mail" size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground">{client?.email}</span>
          </div>
          <div className="flex items-center gap-3">
            <Icon name="Phone" size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground">{client?.phone}</span>
          </div>
          <div className="flex items-center gap-3">
            <Icon name="MapPin" size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground">{client?.address}</span>
          </div>
        </div>
        {!isEditingContact && (
          <Button
            variant="outline"
            size="sm"
            iconName="Edit"
            onClick={() => setIsEditingContact(true)}
            className="mt-4"
          >
            Editar Contato
          </Button>
        )}
      </div>

      <div className="bg-muted/50 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-foreground mb-4">Informações da Transação</h3>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-sm text-muted-foreground">Nome da Transação:</span>
            <span className="text-sm font-medium text-foreground">{client?.transactionName || '-'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-muted-foreground">Valor da Transação:</span>
            <span className="text-sm font-medium text-foreground">
              {client?.transactionAmount ? formatCurrency(client?.transactionAmount) : '-'}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-muted/50 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-foreground mb-4">Resumo Financeiro</h3>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-sm text-muted-foreground">Valor Total:</span>
            <span className="text-sm font-semibold text-foreground">{formatCurrency(client?.totalValue)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-muted-foreground">Valor Pago:</span>
            <span className="text-sm font-medium text-success">{formatCurrency(client?.paidAmount)}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPayments = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-foreground">Histórico de Pagamentos</h3>
        <Button variant="default" size="sm" iconName="Plus">
          Registrar Pagamento
        </Button>
      </div>
      {client?.paymentHistory?.map((payment) => (
        <div key={payment?.id} className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="text-sm font-medium text-foreground">{payment?.description}</div>
              <div className="text-xs text-muted-foreground mt-1">{formatDate(payment?.date)}</div>
            </div>
            <span className="text-sm font-semibold text-success">{formatCurrency(payment?.amount)}</span>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Icon name="CreditCard" size={14} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{payment?.method}</span>
          </div>
        </div>
      ))}
    </div>
  );

  const renderNotes = () => (
    <div className="space-y-4">
      <div className="space-y-3">
        <Input
          type="text"
          placeholder="Adicionar nova nota..."
          value={newNote}
          onChange={(e) => setNewNote(e?.target?.value)}
        />
        <Button
          variant="default"
          size="sm"
          iconName="Plus"
          onClick={handleAddNote}
          fullWidth
        >
          Adicionar Nota
        </Button>
      </div>
      <div className="space-y-3">
        {client?.notes?.map((note) => (
          <div key={note?.id} className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="text-sm text-foreground">{note?.content}</div>
            </div>
            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
              <Icon name="User" size={12} />
              <span>{note?.author}</span>
              <span>•</span>
              <span>{formatDate(note?.date)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderDocuments = () => (
    <div className="space-y-4">
      <Button variant="default" size="sm" iconName="Upload" fullWidth>
        Upload Documento
      </Button>
      <div className="space-y-3">
        {client?.documents?.map((doc) => (
          <div key={doc?.id} className="bg-muted/50 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon name="FileText" size={20} className="text-primary" />
              <div>
                <div className="text-sm font-medium text-foreground">{doc?.name}</div>
                <div className="text-xs text-muted-foreground">{doc?.size} • {formatDate(doc?.uploadDate)}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button className="p-2 hover:bg-muted rounded-md transition-colors">
                <Icon name="Download" size={16} className="text-muted-foreground" />
              </button>
              <button className="p-2 hover:bg-muted rounded-md transition-colors">
                <Icon name="Trash2" size={16} className="text-error" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col bg-card border-l border-border">
      <div className="p-4 border-b border-border">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <Image
              src={client?.avatar}
              alt={client?.avatarAlt}
              className="w-12 h-12 rounded-full object-cover"
            />
            <div>
              <h2 className="text-lg font-semibold text-foreground">{client?.name}</h2>
              <p className="text-sm text-muted-foreground">{client?.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-md transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex items-center gap-2">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'payments' && renderPayments()}
        {activeTab === 'notes' && renderNotes()}
        {activeTab === 'documents' && renderDocuments()}
      </div>
      <div className="p-4 border-t border-border">
        <div className="flex gap-2">
          <Button variant="outline" iconName="Mail" fullWidth>
            Enviar Email
          </Button>
          <Button variant="outline" iconName="Phone" fullWidth>
            Ligar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ClientDetails;